import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../assets/css/GameWonModal.css";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "context/LanguageProvider";
import { useDarkMode } from "context/DarkModeProvider";
import { useContext } from "react";
import { UserProgressContext } from "context/UserProgressContext";
import "../assets/css/colors.css";

import {
  fetchLevel,
  fetchXp,
  increaseXp,
  increaseLevel,
  decreaseXp,
  getGameXp,
} from "apiHandles/user_progress";

const GameWonModal = ({ isVisible, onPlayAgain, onExit }) => {
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const isGameDone = localStorage.getItem("gameDone");
  const { selLanguage } = useLanguage();
  const { updateXp, updateLevel } = useContext(UserProgressContext);

  const [xpWon, setXpWon] = useState(0);
  const [totalXp, setTotalXp] = useState(0);
  const [level, setLevel] = useState(1);
  const updatedRef = useRef(false);

  useEffect(() => {
    if (!isVisible) return;

    // Έλεγχος αν δεν πρέπει να δώσουμε XP
    if (!localStorage.getItem("gameFirstTry")) {
      return;
    }

    // Χρησιμοποίησε το selectedStage.id ως unique key για να αποφύγεις διπλό update
    const selectedStageRaw = localStorage.getItem("selectedStage");
    if (!selectedStageRaw) return;

    const selectedStage = JSON.parse(selectedStageRaw);
    const gameXpProcessedKey = `gameXpProcessed_${selectedStage.id}`;

    // Έλεγχος αν έχει ήδη επεξεργαστεί (localStorage + ref για επιπλέον προστασία)
    if (localStorage.getItem(gameXpProcessedKey) === "true" || updatedRef.current) {
      return;
    }

    // Σημαίωσε ότι ξεκίνησε η επεξεργασία
    localStorage.setItem(gameXpProcessedKey, "true");
    updatedRef.current = true;

    window.dispatchEvent(new CustomEvent("gameFinished"));

    const updateXpAndLevel = async () => {
      try {

        const selectedStageRaw = localStorage.getItem("selectedStage");
        if (!selectedStageRaw) {
          return;
        }

        const selectedStage = JSON.parse(selectedStageRaw);
        const stageId = selectedStage.id;

        const [currentXp, currentLevel, gameXp] = await Promise.all([
          fetchXp(),
          fetchLevel(),
          getGameXp(stageId),
        ]);


        const lvl = currentLevel;
        setLevel(lvl);
        setXpWon(gameXp);

        if (!gameXp || gameXp === 0) {
          setTotalXp(currentXp || 0);
          return;
        }

        // Πρόσθεσε τα XP μία φορά
        let updatedXp = await increaseXp(gameXp);

        if (updatedXp == null) {
          setTotalXp(currentXp || 0);
          return;
        }

        const xpRequired = lvl * 100;

        if (updatedXp >= xpRequired) {
          // Level up scenario

          const leftXp = updatedXp - xpRequired;

          // Κάνε level up
          await increaseLevel(1);
          const newLevel = await fetchLevel();

          // Μηδένισε και θέσε τα remaining XP (μία φορά μόνο)
          await decreaseXp();
          await increaseXp(leftXp);

          setLevel(newLevel ?? lvl + 1);
          setTotalXp(leftXp);

          // Ενημέρωσε το context
          setTimeout(() => {
            updateLevel(newLevel ?? lvl + 1);
            updateXp(leftXp);
          }, 100);
        } else {
          setTotalXp(updatedXp);

          setTimeout(() => {
            updateXp(updatedXp);
          }, 100);
        }
      } catch (err) {
        const selectedStageRaw = localStorage.getItem("selectedStage");
        if (selectedStageRaw) {
          const selectedStage = JSON.parse(selectedStageRaw);
          localStorage.removeItem(`gameXpProcessed_${selectedStage.id}`);
          updatedRef.current = false;
        }
      }
    };

    updateXpAndLevel();
  }, [isVisible]);

  // Καθάρισε το flag όταν φύγεις από το παιχνίδι (exit)
  useEffect(() => {
    const handleGameExit = () => {
      const selectedStageRaw = localStorage.getItem("selectedStage");
      if (selectedStageRaw) {
        const selectedStage = JSON.parse(selectedStageRaw);
        localStorage.removeItem(`gameXpProcessed_${selectedStage.id}`);
      }
      updatedRef.current = false;
    };

    // Άκου για το exit event
    window.addEventListener("gameExit", handleGameExit);

    return () => {
      window.removeEventListener("gameExit", handleGameExit);
    };
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            className="game-won-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />

          <motion.div
            className="game-won-modal"
            initial={{ opacity: 0, scale: 0.7, y: -50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: -50 }}
            style={{
              background: darkMode
                ? "linear-gradient(145deg, #1f2937 0%, #374151 100%)"
                : "var(--lightModeModal)",
            }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300,
              duration: 0.4,
            }}
          >
            <h1 className="game-won-title">YOU WON</h1>

            <p
              className="game-won-message"
              style={{
                color: darkMode ? "#fff" : "#0f0f0fff",
              }}
            >
              {isGameDone === "true" ? (
                selLanguage ? (
                  <>
                    Συγχαρητήρια! Κατάφερες να νικήσεις το παιχνίδι!
                  </>
                ) : (
                  <>
                    Congratulations! You managed to beat the game!
                  </>
                )
              ) : selLanguage ? (
                <>
                  Συγχαρητήρια! Κατάφερες να νικήσεις το παιχνίδι και πήρες{" "}
                  <strong>{xpWon}</strong> XP.
                </>
              ) : (
                <>
                  Congratulations! You managed to beat the game and earned{" "}
                  <strong>{xpWon}</strong> XP.
                </>
              )}
            </p>

            <div className="game-won-buttons">
              <motion.button
                className="game-won-btn play-again-won-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  // Καθάρισε το flag για να μπορεί να παίξει ξανά
                  const selectedStageRaw = localStorage.getItem("selectedStage");
                  if (selectedStageRaw) {
                    const selectedStage = JSON.parse(selectedStageRaw);
                    localStorage.removeItem(`gameXpProcessed_${selectedStage.id}`);
                  }
                  updatedRef.current = false;

                  onPlayAgain();
                }}
              >
                {selLanguage ? "Παίξε Ξανά" : "Play Again"}
              </motion.button>

              <motion.button
                className="game-won-btn exit-won-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  // Καθάρισε το flag πριν φύγεις
                  const selectedStageRaw = localStorage.getItem("selectedStage");
                  if (selectedStageRaw) {
                    const selectedStage = JSON.parse(selectedStageRaw);
                    localStorage.removeItem(`gameXpProcessed_${selectedStage.id}`);
                  }
                  updatedRef.current = false;

                  onExit && onExit();
                  navigate("/admin/index");
                }}
              >
                {selLanguage ? "Έξοδος" : "Exit"}
              </motion.button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default GameWonModal;